import {
  OktonEmptyState,
  OktonSearchFilter,
  OktonSelect,
} from '@okton/platform-ui';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Flex,
  Button,
  SimpleGrid,
  useColorModeValue,
  Badge,
} from '@chakra-ui/react';
import { RefreshCw, UploadCloud } from 'lucide-react';
import { OktonReactTable as Table } from '@okton/platform-ui/react-table';

import api from '../../services/api';
import Page from '../../layout/IntegraAppPage';

type RouteParams = {
  batchId: string;
};

type StatusSincronizacao = 'pendente' | 'processando' | 'sucesso' | 'erro';

type ItemSincronizacao = {
  id: number;
  produto: string;
  sku: string;
  status: StatusSincronizacao;
  mensagem: string;
  anuncioId?: string | null;
  atualizadoEm: string;
};

type ErroApi = {
  message?: string;
  error?: string;
};

type ItemSincronizacaoApi = {
  id: number;
  produto_id: number;
  produto?: {
    titulo_interno?: string;
    sku?: string;
  };
  status?: string;
  id_externo?: string | null;
  updated_at?: string;
  ultimo_erro?: unknown;
  dados_adicionais?: {
    ultimo_erro?: unknown;
    erro_api?: ErroApi | string;
  };
};

type DadosLote = {
  items?: ItemSincronizacaoApi[];
};

type CardResumoProps = {
  titulo: string;
  valor: number;
  cor: string;
};

const CardResumo = ({ titulo, valor, cor }: CardResumoProps) => (
  <Box
    bg="white"
    border="1px solid"
    borderColor="gray.200"
    borderRadius="xl"
    p={5}
  >
    <Text fontSize="sm" color="gray.500">
      {titulo}
    </Text>
    <Text fontSize="2xl" fontWeight="700" color={cor}>
      {valor}
    </Text>
  </Box>
);

const SincronizacaoLote = () => {
  const { batchId } = useParams<RouteParams>();

  const [busca, setBusca] = useState('');
  const [statusFiltro, setStatusFiltro] = useState<'todos' | string>('todos');
  const [estaCarregando, setEstaCarregando] = useState(false);
  const [dadosLote, setDadosLote] = useState<DadosLote | null>(null);

  const corDeFundo = useColorModeValue('white', 'gray.800');
  const corDaBorda = useColorModeValue('gray.200', 'gray.700');
  const corDoTextoSecundario = useColorModeValue('gray.500', 'gray.400');

  const carregarDados = async () => {
    setEstaCarregando(true);

    try {
      const response = await api.get('/produtos-anuncios');

      setDadosLote(response.data);
    } catch {
      // A tela permanece no último estado válido; uma nova tentativa pode ser feita manualmente.
    } finally {
      setEstaCarregando(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, [batchId]);

  const itensBrutos = dadosLote?.items || [];

  const formatarErro = (item: ItemSincronizacaoApi) => {
    const erroApi = item.dados_adicionais?.erro_api;
    const erroApiDetalhe =
      typeof erroApi === 'object' && erroApi
        ? erroApi.message || erroApi.error || erroApi
        : erroApi;
    const erro =
      item.ultimo_erro || item.dados_adicionais?.ultimo_erro || erroApiDetalhe;

    if (!erro) {
      return null;
    }

    if (typeof erro === 'string') {
      return erro;
    }

    return JSON.stringify(erro);
  };

  const itens: ItemSincronizacao[] = itensBrutos.map(item => {
    const erro = formatarErro(item);

    return {
      id: item.id,
      produto: item.produto?.titulo_interno || `Produto #${item.produto_id}`,
      sku: item.produto?.sku || `ID-${item.produto_id}`,
      status: erro
        ? 'erro'
        : item.status === 'sincronizado'
        ? 'sucesso'
        : 'processando',
      mensagem: erro || item.status || 'Aguardando processamento',
      anuncioId: item.id_externo || null,
      atualizadoEm: item.updated_at || '-',
    };
  });

  useEffect(() => {
    const temItensEmAndamento =
      itens.length > 0 &&
      itens.some(
        item => item.status === 'pendente' || item.status === 'processando',
      );

    if (!temItensEmAndamento) {
      return undefined;
    }

    const intervalo = setInterval(() => {
      carregarDados();
    }, 3000);

    return () => clearInterval(intervalo);
  }, [itens, batchId]);

  const itensFiltrados = useMemo(() => {
    return itens.filter(item => {
      const termo = busca.toLowerCase();

      const bateBusca =
        item.produto.toLowerCase().includes(termo) ||
        item.sku.toLowerCase().includes(termo) ||
        item.mensagem.toLowerCase().includes(termo) ||
        (item.anuncioId || '').toLowerCase().includes(termo);

      const bateStatus =
        statusFiltro === 'todos' || item.status === statusFiltro;

      return bateBusca && bateStatus;
    });
  }, [itens, busca, statusFiltro]);

  const total = itens.length;
  const concluidos = itens.filter(item => item.status === 'sucesso').length;
  const comErro = itens.filter(item => item.status === 'erro').length;
  const pendentes = itens.filter(
    item => item.status === 'pendente' || item.status === 'processando',
  ).length;

  const colunas = useMemo(
    () => [
      {
        Header: 'PRODUTO',
        accessor: 'produto',
        Cell: ({ row }: { row: { original: ItemSincronizacao } }) => (
          <Box>
            <Text fontWeight="600">{row.original.produto}</Text>
            <Text fontSize="xs" color="gray.500">
              SKU: {row.original.sku}
            </Text>
          </Box>
        ),
      },
      {
        Header: 'STATUS',
        accessor: 'status',
        Cell: ({ value }: { value: StatusSincronizacao }) => {
          const mapa: Record<StatusSincronizacao, string> = {
            pendente: 'gray',
            processando: 'blue',
            sucesso: 'green',
            erro: 'red',
          };

          return (
            <Badge colorScheme={mapa[value] || 'gray'} borderRadius="full">
              {String(value).toUpperCase()}
            </Badge>
          );
        },
      },
      {
        Header: 'MENSAGEM',
        accessor: 'mensagem',
        Cell: ({ value }: { value: string }) => (
          <Text fontSize="sm">{String(value)}</Text>
        ),
      },
      {
        Header: 'ID DO ANÚNCIO',
        accessor: 'anuncioId',
        Cell: ({ value }: { value?: string | null }) => (
          <Text fontSize="sm">{value || '-'}</Text>
        ),
      },
      {
        Header: 'ATUALIZADO EM',
        accessor: 'atualizadoEm',
        Cell: ({ value }: { value: string }) => (
          <Text fontSize="sm">{String(value)}</Text>
        ),
      },
    ],
    [],
  );

  return (
    <Page>
      <Box mb={6}>
        <Heading size="lg" mb={1} textAlign="left">
          Sincronização em lote
        </Heading>
        <Text fontSize="sm" color={corDoTextoSecundario}>
          Acompanhamento do envio de produtos para o Mercado Livre
        </Text>
        <Text fontSize="xs" color={corDoTextoSecundario} mt={1}>
          Lote: {batchId}
        </Text>
        <Text fontSize="xs" color={corDoTextoSecundario}>
          Itens carregados: {total}
        </Text>
      </Box>

      <SimpleGrid columns={[1, 2, 4]} spacing={5} mb={6}>
        <CardResumo titulo="Total" valor={total} cor="blue.500" />
        <CardResumo titulo="Concluídos" valor={concluidos} cor="green.500" />
        <CardResumo titulo="Com erro" valor={comErro} cor="red.500" />
        <CardResumo titulo="Pendentes" valor={pendentes} cor="orange.500" />
      </SimpleGrid>

      <Box
        bg={corDeFundo}
        border="1px solid"
        borderColor={corDaBorda}
        borderRadius="2xl"
        overflow="hidden"
      >
        <Box p={6} borderBottom="1px solid" borderColor={corDaBorda}>
          <Flex gap={3} wrap="wrap" align="center">
            <OktonSearchFilter
              value={busca}
              onChange={setBusca}
              placeholder="Buscar por produto, SKU, mensagem ou anúncio..."
            />

            <OktonSelect
              name="status"
              value={statusFiltro}
              onChange={event => setStatusFiltro(event.target.value)}
              options={[
                { value: 'todos', label: 'Todos os status' },
                { value: 'pendente', label: 'Pendente' },
                { value: 'processando', label: 'Processando' },
                { value: 'sucesso', label: 'Sucesso' },
                { value: 'erro', label: 'Erro' },
              ]}
              w="200px"
            />

            <Button
              leftIcon={<RefreshCw size={14} />}
              size="sm"
              bg="blue.500"
              color="white"
              isLoading={estaCarregando}
              onClick={carregarDados}
            >
              Atualizar
            </Button>

            <Button
              leftIcon={<UploadCloud size={14} />}
              size="sm"
              variant="outline"
              colorScheme="blue"
            >
              Tentar novamente falhas
            </Button>
          </Flex>
        </Box>

        <Box p={4}>
          <Table columns={colunas} data={itensFiltrados} />

          {itensFiltrados.length === 0 && (
            <OktonEmptyState
              title="Nenhum item encontrado"
              description="Não há itens de sincronização para os filtros atuais."
            />
          )}
        </Box>
      </Box>
    </Page>
  );
};

export default SincronizacaoLote;
